import { jSith } from "../jquery-replacement";
import { LooseAutocomplete, NestedArray, PickByType } from "../../pojo/Util";
import { S25ObjectUtil } from "./s25.object.util";
import { S25Number } from "./s25.number";
import { isArray, isObject, isUndefined } from "./index";

export namespace S25Array {
    export function toItemIds(arr: { itemId?: string | number }[]): number[] {
        return arr.map((obj) => (obj.itemId ? S25Number.parseInt(obj.itemId) : NaN));
    }
    //https://stackoverflow.com/questions/2450954/how-to-randomize-shuffle-a-javascript-array
    export function shuffle(array: any[]): any[] {
        for (let i = array.length - 1; i > 0; i--) {
            //last element skipped bc no other choices to swap at that point
            const j: number = Math.floor(Math.random() * (i + 1));
            const temp: any = array[i];
            array[i] = array[j];
            array[j] = temp;
        }
        return array;
    }

    export function getNumbers(num: number): any[] {
        num = isNaN(num) ? 0 : num;
        num = Math.ceil(num);
        if (num <= 0) {
            return [];
        }
        return new Array(num);
    }

    export function validNumbers(arr: (number | null | undefined)[]): number[] {
        return arr.filter((item): item is number => typeof item === "number" && !isNaN(item));
    }

    /**
     * Return an array of indexes from 0 to num-1, e.g. num=3 returns [0,1,2]
     */
    export function getIndexArray(num: number): number[] {
        num = isNaN(num) ? 0 : num;
        num = Math.ceil(num);
        if (num <= 0) {
            return [];
        }
        return new Array(num).fill(0).map((_, idx) => idx);
    }

    export function isEmpty(arr: any[]): boolean {
        return !arr || arr.length === 0;
    }

    export function forceArray<T, U = [T] extends [undefined] ? unknown[] : T extends any[] ? T : T[]>(obj?: T): U {
        if (!isArray(obj)) {
            if (isUndefined(obj)) {
                return [] as any;
            } else {
                return [obj] as any;
            }
        }
        return obj as any;
    }

    export function increment(array: any[], step: number): any[] {
        array.forEach(function (val: any, i: number) {
            array[i] = val + step;
        });
        return array;
    }

    export function isEqual<T>(arr1: T[], arr2: T[], hash?: (item: T) => any): boolean {
        if (!arr1 && !arr2) return true;
        if (!arr1 || !arr2) return false;
        if (arr1.length !== arr2.length) return false;

        return arr1.every((item, i) => {
            const item2 = arr2[i];
            if (hash) return hash(item) === hash(item2);
            return item === item2;
        });
    }

    export function isEqualDeep(arr1: any[], arr2: any[]): boolean {
        return uniqueDeep([arr1, arr2]).length === 1;
    }

    /**
     * Checks whether two arrays contain all the same items
     */
    export function allSame<T>(arr1: readonly T[], arr2: readonly T[], hash?: (item: T) => unknown): boolean {
        return !hasSymmetricDifference(arr1, arr2, hash);
    }

    export function clone<T extends any[] | undefined = any[]>(arr: T): T {
        if (isUndefined(arr)) {
            return arr;
        }
        return <T>arr!.slice(0);
    }

    export function insert(arr: any[], position: number, value: any) {
        arr.splice(position, 0, value);
    }

    export function remove(arr: any[], position: number) {
        arr.splice(position, 1);
    }

    export function move(arr: any[], fromIndex: number, toIndex: number) {
        if (toIndex || toIndex === 0) {
            const element = arr[fromIndex];
            arr.splice(fromIndex, 1);
            arr.splice(toIndex, 0, element);
        } else {
            remove(arr, fromIndex);
        }
        return arr;
    }

    export function injectArray(dst: any[], index: number, arrayToInsert: any[]) {
        Array.prototype.splice.apply(dst, [index, 0].concat(arrayToInsert));
    }

    export function unique<Item, Key = Item>(arr: Item[], hash?: (item: Item) => Key): Item[] {
        if (isUndefined(arr)) return [];
        const seen = new Set<Key>();
        return arr.filter((item) => {
            const key = typeof hash === "function" ? hash(item) : (item as unknown as Key);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }

    export function uniqueDeep(array: any[]): any[] {
        const orig: any[] = array.concat();
        const checker: any = {};
        const ret: any[] = [];
        for (let i = 0; i < orig.length; i++) {
            const jsonstring = S25ObjectUtil.stringify(orig[i]); //use json stringify to uniquely identify array value
            if (!checker[jsonstring]) {
                //hash comparison, very fast
                checker[jsonstring] = "taken"; //add to hash table
                ret.push(orig[i]); //add to unique return array
            }
        }
        return ret;
    }

    export function addUnique<T>(array: readonly T[], item: T): readonly T[] {
        return array.includes(item) ? array : [...array, item];
    }

    export function findByProp(arr: any[] | undefined, prop: any, value: any): number {
        if (arr && arr.length > 0) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i][prop] == value) {
                    return i;
                }
            }
        }
        return -1;
    }

    export function getByProp(arr: any[] | undefined, prop: any, value: any): any {
        const i: number = findByProp(arr, prop, value);
        return i > -1 ? arr![i] : null;
    }

    export function inplaceRemoveByProp(arr: any[] | undefined, prop: any, value: any) {
        const position: number = findByProp(arr, prop, value);
        if (position === -1) {
            //not found - nothing to remove
            return;
        }
        remove(arr!, position);
    }

    export function inplacePushByProp(arr: any[], prop: any, value: any, valueItem: any) {
        const position: number = findByProp(arr, prop, value);
        if (position === -1) {
            //not found, so add
            arr.push(valueItem);
            return true;
        }
        return null; //found, so do not add
    }

    export function inplaceFilter<T>(arr: T[], filter: (item: T) => boolean, shortCircuit?: boolean) {
        for (let i = arr.length - 1; i >= 0; i--) {
            if (!filter(arr[i])) {
                arr.splice(i, 1);
                if (shortCircuit) return;
            }
        }
    }

    export function uniqueByProp(arr: any[], prop: any): any[] {
        const exists: any = {}; //keep track of what exists in the array
        return arr.filter(function (item: any) {
            if (typeof exists[item[prop]] !== "undefined") {
                //if it already exists, do not include it again
                return false;
            } else {
                //else, add it to the exists list and include it once
                exists[item[prop]] = true;
                return true;
            }
        });
    }

    export function isIn(arr: any[] | undefined, prop: any, value: any): boolean {
        if (!arr || arr.length == 0) {
            return false;
        }

        for (let i = 0; i < arr.length; ++i) {
            const item: any = arr[i];
            if (item[prop] == value) {
                return true;
            }
        }

        return false;
    }

    export function isIntDivisor(divisor: number, array: any[]): boolean {
        if (isUndefined(divisor)) {
            return false;
        }

        for (let i = 0; i < array.length; ++i) {
            if (array[i] % divisor !== 0) {
                return false;
            }
        }

        return true;
    }

    /**
     * Checks whether two arrays have any intersection. Comparison is made by reference. Short circuits.
     * @param arr1
     * @param arr2
     */
    export function hasIntersection<T>(arr1: T[], arr2: T[], hash?: (item: T) => unknown): boolean {
        const arr1Set = new Set(arr1.map(hash ? hash : (item) => item));
        return arr2.some((item) => arr1Set.has(hash ? hash(item) : item));
    }

    /**
     * Returns the intersection of two arrays. Comparison is made by reference
     * @param arr1
     * @param arr2
     */
    export function intersection<T>(arr1: T[], arr2: T[]) {
        const arr1Set = new Set(arr1);
        return arr2.filter((item) => arr1Set.has(item));
    }

    /**
     * Checks whether two arrays contain any different items. Short circuits.
     * @param arr1
     * @param arr2
     */
    export function hasSymmetricDifference<T, H>(
        arr1: readonly T[],
        arr2: readonly T[],
        hash?: (item: T) => H,
    ): boolean {
        if (arr1.length !== arr2.length) return true;
        hash ??= (item) => item as unknown as H;
        const arr1Set = new Set(arr1.map(hash!));
        const arr2Set = new Set(arr2.map(hash!));
        if (arr1.some((item) => !arr2Set.has(hash!(item)))) return true;
        if (arr2.some((item) => !arr1Set.has(hash!(item)))) return true;
        return false;
    }

    /**
     * Returns the items that are in arr1 or arr2 but not in both
     * @param arr1
     * @param arr2
     */
    export function symmetricDifference<T>(arr1: T[], arr2: T[], hash?: (item: T) => unknown): T[] {
        hash ??= (item) => item;
        const arr1Set = new Set(arr1.map(hash!));
        const arr2Set = new Set(arr2.map(hash!));
        return [
            ...arr1.filter((item) => !arr2Set.has(hash!(item))),
            ...arr2.filter((item) => !arr1Set.has(hash!(item))),
        ];
    }

    /**
     * Returns the items in arr2 that are not in arr1
     * @param arr1
     * @param arr2
     */
    export function relativeComplement<T>(arr1: T[], arr2: T[], hash?: (item: T) => unknown): T[] {
        hash ??= (item) => item;
        const arr1Set = new Set(arr1.map(hash!));
        return arr2.filter((item) => !arr1Set.has(hash!(item)));
    }

    //returns all items in arr1 that are also in arr2 by list of supplied properties, carryProps is an optional list of props to carry over from items in arr2
    export function shallowIntersection(arr1: any[], arr2: any[], props: any[], carryProps?: string[]): any[] {
        const ret: any = [];
        props = forceArray(props);
        jSith.forEach(arr1, function (_: any, item1: any) {
            jSith.forEach(arr2, function (_: any, item2: any) {
                let isIntersect = props.length > 0;
                jSith.forEach(props, function (_: any, prop: any) {
                    const value1 = isObject(item1) ? item1[prop] : item1;
                    const value2 = isObject(item2) ? item2[prop] : item2;
                    isIntersect = isIntersect && value1 == value2;
                });
                if (isIntersect) {
                    carryProps?.forEach((prop: string) => {
                        if (item2[prop]) item1[prop] = item2[prop];
                    });
                    ret.push(item1);
                }
            });
        });
        return unique(ret);
    }

    //Combines two arrays, and app applies status properties to the second
    export function applyStatus(src: any[], dst: any[]) {
        src = forceArray(src);
        dst = forceArray(dst);

        dst.forEach((item: any) => {
            item.status = "del";
        });

        src.forEach((item) => {
            const obj = getByProp(dst, "itemId", item.itemId);
            if (obj) {
                obj.status = "est";
            } else {
                item.status = "new";
                dst.push(item);
            }
        });
        return dst;
    }

    export function last<T>(arr: T[]): T {
        return arr.length && arr[arr.length - 1];
    }

    export function flatten<T>(arr: NestedArray<T>, map?: (item: T) => T | NestedArray<T>): T[] {
        if (!arr.reduce) return [arr as unknown as T]; // Not a real array
        return arr.reduce<any>((flat: T[], elem: T | NestedArray<T>) => {
            if (!isArray(elem) && map) elem = map(elem as T); // Map if possible
            if (!isArray(elem)) return flat.concat([elem as T]);
            return flat.concat(flatten(elem as NestedArray<T>, map));
        }, []);
    }

    // Breaks overlapping items into "tracks" of non-overlapping items
    export function getTracks<
        Item extends unknown,
        NumProps extends keyof PickByType<Item, number>,
        Start extends LooseAutocomplete<NumProps>,
        End extends LooseAutocomplete<NumProps>,
    >(items: Item[], startProp: Start, endProp: End, transform?: (item: Item) => Record<string, number>): Item[][] {
        if (!items.length) return [[]];

        // @ts-ignore
        const getVal = (item: Item, prop: any) => (transform ? transform(item) : item)[prop] as number;
        items.sort((a, b) => getVal(a, startProp) - getVal(b, startProp));

        const tracks: Item[][] = [];
        for (const item of items) {
            const itemVal = getVal(item, startProp);
            const track = tracks.find((track) => {
                const lastItemVal = getVal(track[track.length - 1], endProp);
                return lastItemVal <= itemVal;
            });
            if (!track) tracks.push([item]);
            else track.push(item);
        }

        return tracks;
    }

    /**
     * Counts the number of items for which the countingFunction returns true
     * @param arr Array of items to count
     * @param countingFunction Callback returning a number for how much to count
     * @return {number}
     */
    export function count<T>(arr: readonly T[], countingFunction: (item: T) => number): number {
        return arr.reduce((count, item) => count + countingFunction(item), 0);
    }

    export function max<T>(arr: T[], transform: (item: T) => number): number {
        return arr.reduce((max, item) => Math.max(max, transform(item)), -Infinity);
    }

    /**
     * Finds the max depth of an array
     * @param arr Array to find the depth of
     * @param map Function which maps an item to the next array
     * @param filter Function which filters out undesirable paths
     */
    export function depth<T extends any>(
        arr: T[],
        map?: (item: T) => T[] | undefined,
        filter?: (item: T) => boolean,
    ): number {
        if (!isArray(arr)) return 0;
        return (
            Math.max(
                0,
                ...arr.map((item) => {
                    if (filter && !filter(item)) return 0;
                    const itemArr = map ? map(item) : (item as T[]);
                    if (!itemArr) return 0;
                    return depth(itemArr, map);
                }),
            ) + 1
        );
    }

    /**
     * Groups array items by keys provided by the passed grouping function
     */
    export function groupBy<T, Prop extends PropertyKey>(
        arr: T[] | undefined,
        grouper: (item: T) => Prop,
    ): Partial<{ [Key in Prop]: T[] }> {
        const groups = {} as { [Key in Prop]: T[] };
        if (!arr) return groups;
        for (const item of arr || []) {
            const key = grouper(item);
            if (!groups[key]) groups[key] = [];
            groups[key].push(item);
        }
        return groups;
    }

    export function mapBy<T, Prop>(arr: T[], keyMap: (item: T) => Prop): Map<Prop, T> {
        const map: Map<Prop, T> = new Map();
        for (const item of arr || []) map.set(keyMap(item), item);
        return map;
    }

    /**
     * Reverses an array in place between two indices
     * @param arr The array to be partially reversed
     * @param start {number} The first index to be reversed
     * @param end {number} The last index to be reversed
     * @returns The original array modified in place
     * @time O(n)
     * @space O(1)
     */
    export function reverse<T>(arr: T[], start?: number, end?: number): T[] {
        start = S25Number.clamp(start ?? 0, 0, arr.length - 1);
        end = S25Number.clamp(end ?? arr.length - 1, 0, arr.length - 1);
        while (start < end) {
            [arr[start], arr[end]] = [arr[end], arr[start]]; // Swap
            [start, end] = [start + 1, end - 1]; // Increment/Decrement
        }
        return arr;
    }

    /**
     * Rotates an array in place by "count" steps
     * @param arr The array to be rotated
     * @param count {number} Number of steps to rotate
     * @returns The original array modified in place
     * @time O(3n)
     * @space O(1)
     */
    export function rotate<T>(arr: T[], count: number): T[] {
        count = ((count % arr.length) + arr.length) % arr.length; // (+ arr.length) % arr.length to deal with negative counts
        if (count === 0) return arr; // No rotation needed
        reverse(arr); // Reverse whole array
        reverse(arr, 0, count - 1); // Reverse first part
        reverse(arr, count); // Reverse second part
        return arr;
    }

    /**
     * Iterates over and enumerates an array
     * @param arr
     */
    export function* enumerate<T>(arr: T[]) {
        if (!arr?.length) return;
        for (const i of range(arr.length)) yield [i, arr[i]] as const;
    }

    export function* batch<T>(arr: T[], batchSize: number) {
        if (!arr?.length) return;
        let i = 0;
        while (i < arr.length) {
            yield arr.slice(i, i + batchSize);
            i += batchSize;
        }
    }

    export function findLastIndex<T>(arr: T[], matcher: (item: T) => boolean): number {
        if (!arr?.length) return -1;
        for (let i = arr.length - 1; i >= 0; i--) {
            if (matcher(arr[i])) return i;
        }
        return -1;
    }

    export function toSpliced<T>(arr: T[], index: number, deleteCount: number, ...items: T[]): T[] {
        if (!arr) return [];
        const newArr = [...arr];
        newArr.splice(index, deleteCount, ...items);
        return newArr;
    }
    export function sum(arr: unknown[]): number;
    export function sum<T extends { [_ in P]?: unknown }, P extends PropertyKey>(arr: T[], prop: P): number;
    /**
     * Sums the items of an array.
     * @param arr An array containing any type
     * @param [prop] If arr contains object, then this property is used to get values out of the objects
     * @return {number}
     */
    export function sum<T>(arr: T[], prop?: PropertyKey): number {
        if (prop) {
            return arr.reduce((sum, item: any) => sum + (S25Number.parseFloat(item?.[prop]) || 0), 0);
        } else {
            return arr.reduce((sum, item) => sum + (S25Number.parseFloat(item) || 0), 0);
        }
    }

    export function range(stop: number): Generator<number, void, unknown>;
    export function range(start: number, stop: number, step?: number): Generator<number, void, unknown>;
    export function* range(startOrStop: number, stop: number = null, step = 1) {
        if (typeof stop !== "number") {
            stop = startOrStop;
            startOrStop = 0;
        }
        for (let i = startOrStop; i < stop; i += step) yield i;
    }

    export function findBoundaries(array: any[], number: number): { min: number; max: number } {
        number = S25Number.toInt(number);

        if (!array || !array.length) {
            return { min: Math.min(0, number), max: Math.max(0, number) };
        } else if (array.length === 1) {
            return { min: Math.min(0, array[0], number), max: Math.max(0, array[0], number) };
        }

        let max: number, min: number;
        // array must be sorted in asc order!
        for (let i = 0; i < array.length; ++i) {
            const value = S25Number.toInt(array[i]);
            if (number < value) {
                max = value;
                min = S25Number.toInt(array[i - 1]);
                break;
            }
        }
        if (isUndefined(max)) {
            max = S25Number.toInt(array[array.length - 1]);
            min = S25Number.toInt(array[array.length - 2]);
        }
        return { min: min, max: max };
    }

    export function isInBoundary(direction: string, gap: number, boundaryValue: number, value: number): boolean {
        if (direction === "left") {
            if (value - gap < boundaryValue) {
                return false;
            }
        } else {
            if (value + gap > boundaryValue) {
                return false;
            }
        }
        return true;
    }
}
